import { memo, useState } from "react";
import type { FormEvent } from "react";
import { Alert, Button, Col, FormControl, Row, Well } from "react-bootstrap";
import { Sidebar } from "ClientApp/components/layout/sidebar";
import { useForm } from "ClientApp/utils/rest-api";
import type { IDonorMentorCannedResponse } from "@nkr/models/dash/donor-mentor-canned-response";
import { DonorMentorCannedResponseApi } from "@nkr/models/dash/donor-mentor-canned-response";

const DEFAULT_CONTENT = "Hi {CandidateFirstName},\r\n\r\nI hope you are doing well.\r\n\r\nKind regards,\r\n{UserFullName}";
const DELETE_CONFIRMATION_MESSAGE = "The canned response will be permanently deleted. Click Delete again to confirm.";
const EMAIL_CONTENT_REQUIRED_MESSAGE = "Please update the Email Content.";

interface IProps {
    selected: IDonorMentorCannedResponse | null,
    setSelectedRow: (selected: IDonorMentorCannedResponse | null) => void,
}

export const DonorMentorCannedResponsesSidebar = (props: IProps) => {
    if (!props.selected) {
        return null;
    }

    return (
        <Sidebar title="Donor Mentor Canned Response" isOpen={true} onClose={() => props.setSelectedRow(null)}>
            <SidebarBody selected={props.selected} setSelectedRow={props.setSelectedRow} />
        </Sidebar>
    );
};

const SidebarBody = memo((props: IProps) => {
    const data = props.selected;
    if (!data) {
        return null;
    }

    const form = useForm<IDonorMentorCannedResponse>(DonorMentorCannedResponseApi, data.id || null);
    const deleteHook = DonorMentorCannedResponseApi.useDelete(data.id || null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    form.setOnSaveComplete((saved: IDonorMentorCannedResponse) => {
        props.setSelectedRow(saved);
    });

    const deleteRecord = async () => {
        if (!data.id) {
            return;
        }

        if (!showDeleteConfirmation) {
            setShowDeleteConfirmation(true);
            return;
        }

        const result = await deleteHook.submit();
        if (result.isSuccess) {
            props.setSelectedRow(null);
            return;
        }

        setErrorMessage(result.message || "Unable to delete donor mentor canned response.");
    };

    const onSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!form.data.emailContent?.trim()) {
            setErrorMessage(EMAIL_CONTENT_REQUIRED_MESSAGE);
            return;
        }

        setErrorMessage("");
        setShowDeleteConfirmation(false);
        form.handleSubmit(e);
    };

    return (
        <Well bsSize="small" style={{ minHeight: "calc(100vh - 90px)", marginBottom: 0, display: "flex", flexDirection: "column" }}>
            {!form.isLoaded && "Loading..."}

            {form.isLoaded && form.data && (
                <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    {errorMessage && (
                        <Alert bsStyle="danger">{errorMessage}</Alert>
                    )}

                    {showDeleteConfirmation && (
                        <Alert bsStyle="warning">{DELETE_CONFIRMATION_MESSAGE}</Alert>
                    )}

                    <Row style={{ marginBottom: 10 }}>
                        <Col xs={12}><strong>Name:</strong></Col>
                        <Col xs={12}><FormControl required name="name" type="text" value={form.data.name ?? ""} onChange={form.handleStringChange} /></Col>
                    </Row>

                    <Row style={{ marginBottom: 10 }}>
                        <Col xs={12}><strong>Email Subject:</strong></Col>
                        <Col xs={12}><FormControl required name="emailSubject" type="text" value={form.data.emailSubject ?? ""} onChange={form.handleStringChange} /></Col>
                    </Row>

                    <Row style={{ marginBottom: 10 }}>
                        <Col xs={12}><strong>Email Content:</strong></Col>
                        <Col xs={12}><FormControl required componentClass="textarea" name="emailContent" value={form.data.emailContent ?? DEFAULT_CONTENT} onChange={form.handleStringChange} style={{ height: "calc(100vh - 360px)", minHeight: 220 }} /></Col>
                    </Row>

                    <Row style={{ marginBottom: 10 }}>
                        <Col xs={12}>
                            <small>Links use markdown syntax: [Link text](https://example.com)</small>
                        </Col>
                        <Col xs={12}>
                            <small>Available variables: {"{CandidateFirstName}"}, {"{UserFullName}"}, {"{DASHLOGIN}"} (candidate's Dashboard login link, filled in when the email is sent)</small>
                        </Col>
                    </Row>

                    <Row style={{ marginTop: "auto" }}>
                        <Col xs={6}><Button bsStyle="primary" type="submit">{form.data.id > 0 ? "Update" : "Create"}</Button></Col>
                        {form.data.id && (
                            <Col xs={6} style={{ textAlign: "right" }}><Button bsStyle="danger" onClick={deleteRecord}>Delete</Button></Col>
                        )}
                    </Row>
                </form>
            )}
        </Well>
    );
}, (prev, next) => prev.selected?.id === next.selected?.id);
